// src/lib/agentValue.js — the agent score in LARI: value created for the agency minus resources
// spent, every rate measured from the whole agency (owner rulings 2026-09-29).
//
//   "Every listing costs 0.1 lari and we need to consider how much the income is — that should
//    decide how much weight each must have. So agent spends resource but also delivers."
//   "Keep agents that give us positive value rather than drain us. Someone calling and filling the
//    database is positive, someone bringing money is positive. Someone attending and resharing is
//    positive if the two other things are present; if not, that becomes resource waste."
//   "We are talking about comparative data, not fixed." — "A remote agent not doing anything in the
//    system is a big leak: they may be going to meetings and closing deals." — "Use all agents and
//    all offices for calculations and have a gate for something that may be polluting data."
//
// So:
//   money   = the agent's own commission share (actual ₾) + meetings with an outcome × the agency's
//             measured value of a meeting + followed-up clients × the value of a client
//   owner   = owner-information items × the measured value of an item, capped per month
//             (uncapped, one agent's 1,522 monthly items out-valued five deals)
//   coops   = opened cooperations, weighted ABOVE an owner item and under their own cap, so
//             restriction / terms / detail volume can never crowd them out (owner 2026-11-03:
//             "bigger emphasis on opened cooperations"). A rich cooperation (owner-only
//             information) is coop_rich_weight × an item, a calculator-defaults one
//             coop_default_weight × an item. Counted inside `owner` on every surface.
//   reposts = every paid myhome/ss repost on the agent's account costs the agency's price
//             (0.1 ₾), always. Reposting is automated now (share planner), so a repost says nothing
//             about the agent — it is only what the agency spent on them (owner 2026-11-03:
//             "reshare makes no sense anymore … the more we spend on an agent, we should take
//             their scores"). Until then reposts were free for a productive agent. korter and
//             Facebook are free and never counted.
//   silent  = a scheduled working day with no trace in the system costs the office's value of a
//             working day — in full when remote (the leak), at a share when in the office, and the
//             same share for a day marked absent. MANAGEMENT-ONLY: agents see their total, never
//             this part (owner: "not telling them directly how they are evaluated").
//   late    = every late arrival (first check-in past the late threshold) costs a share of the
//             office's value of a working day (owner 2026-10-01: "bad arrival must make them lose
//             points"). Until then lateness cost nothing — a late day counted as attended. Same
//             management-only rule as silent days. Owner, same day: "late arrival is the same bad
//             thing as silent working hours", answered "both" — so it is two costs: a fixed one
//             per late arrival (late_weight), PLUS the minutes late counted as silent office time
//             (lateMins / the agent's scheduled day length × office_silent_weight). Ten minutes
//             late costs little beyond the fixed part; three hours late costs most of a silent day.
//
// Every rate (value of a deal, meeting, client, item, working day) comes from the REFERENCE: all
// agents of all offices, minus rows that would pollute it (the gate below). The rates move as the
// agency's results move; nothing here is a fixed price except the two the owner set (repost cost,
// and the weights of silent/absent/late days).
import { computeAgentStats } from './statsCompute.js'
import { buildLedger } from './statsModel.js'
import { mergeDealSources } from './performance.js'
import { isManagerRole } from './rankingScore.js'

export const VALUE_DEFAULTS = {
  repost_cost: 0.1,             // ₾ per paid repost (myhome.ge / ss.ge); korter and Facebook are free
  owner_cap_month: 800,         // ₾ ceiling on owner-work value per 30 days (scaled to the window), cooperations excluded
  coop_rich_weight: 10,         // a cooperation with owner-only information = this × the value of an owner item
  coop_default_weight: 3,       // a cooperation with calculator defaults only = this × an item
  coop_cap_month: 1500,         // ₾ ceiling on cooperation value per 30 days (scaled to the window)
  remote_silent_weight: 1,      // a silent scheduled remote day costs this × the value of a working day
  office_silent_weight: 0.5,    // …an office day, which a manager can at least see
  absent_weight: 0.5,           // …a day marked absent
  late_weight: 0.25,            // …a late arrival, fixed part (the minutes are charged on top, as silent office time)
  values_window_days: 90,       // the reference window the rates are measured over
  min_days: 7,                  // the gate: fewer days on the board → not in the reference
  // Used only when the reference is too thin to measure (fewer than 5 deals): the rates measured
  // on 2026-09-29 over 2026-08-01 → 09-28 (28 deals, 804 ₾ per deal).
  fallback: { deal: 804, meeting: 173, client: 142, item: 2.75 },
}

const n = (o, k) => Number(o?.[k]) || 0
// ownerItems stays the rate's denominator (the measured value of an item); scoring splits it into
// cooperations and the rest.
export const ownerItems = a => n(a, 'coop_rich') + n(a, 'restriction_listings') + n(a, 'terms_listings') + n(a, 'detail_properties')
export const coopCount = a => n(a, 'coop_rich') + n(a, 'coop_default')

// THE GATE — which rows may shape the reference. Excluded from the RATES and the MEDIAN (never from
// being scored or judged): managers, agents with fewer than min_days on the board, rows with no
// trace at all in the window (test / ghost accounts), and — for the per-day value — agents with no
// schedule (their working days are unknowable, so a per-day figure would be invented).
export function inReference(row, cfg, { needSchedule = true } = {}) {
  const C = { ...VALUE_DEFAULTS, ...(cfg || {}) }
  if (row.isManager) return false
  if ((row.effDays || 0) < C.min_days) return false
  if (!row.hasTrace) return false
  if (needSchedule && !(row.scheduledDays > 0)) return false
  return true
}

// Rates from the reference rows' own totals: value per deal participation = own commission /
// participations; a meeting = participations per meeting × that; a client and an owner item the
// same way. rows: [{ counts (activity row), closes, commission }] already gated.
export function deriveValues(rows, cfg) {
  const C = { ...VALUE_DEFAULTS, ...(cfg || {}) }
  const sum = f => rows.reduce((s, r) => s + f(r), 0)
  const deals = sum(r => r.closes || 0), income = sum(r => r.commission || 0)
  // Rates divide by ALL meetings and ALL clients taken on (credit is then paid only for the ones with
  // an outcome / a follow-up). Dividing by the smaller credited groups made a client worth more than a
  // meeting — backwards, since a client comes first.
  const meetings = sum(r => n(r.counts, 'meetings')), clients = sum(r => n(r.counts, 'clients'))
  const items = sum(r => ownerItems(r.counts))
  if (deals < 5 || income <= 0) return { ...C.fallback, measured: false, deals, income }
  const deal = income / deals
  const rate = d => d > 0 ? Math.min(1, deals / d) * deal : 0
  // The funnel keeps its order: client ≤ meeting ≤ deal.
  const meeting = Math.min(deal, rate(meetings)), client = Math.min(meeting, rate(clients))
  return { deal: Math.round(deal), meeting: Math.round(meeting), client: Math.round(client),
    item: Math.round(rate(items) * 100) / 100, measured: true, deals, income: Math.round(income) }
}

// One agent's value over a window. counts: activity row; closes/commission from the ledger;
// sd: silentDays() result; values: deriveValues(); dayValue: the office's value of a working day
// (median over the reference of (owner + money) / scheduled days); capScale: window days / 30.
export function agentValue({ counts, closes = 0, commission = 0, sd = null }, values, dayValue, cfg, { capScale = 1 } = {}) {
  // sd: { silentRemote: [dates], silentOffice: [dates], absent: n, late: n, lateMins: n, dayMins: n }
  //     — from computeAgentStats (traceDates)
  const C = { ...VALUE_DEFAULTS, ...(cfg || {}) }
  const items = ownerItems(counts)
  const scale = Math.max(1, capScale)
  const coopsRaw = (n(counts, 'coop_rich') * C.coop_rich_weight + n(counts, 'coop_default') * C.coop_default_weight) * values.item
  const coops = Math.min(coopsRaw, scale * C.coop_cap_month)
  const other = Math.min((items - n(counts, 'coop_rich')) * values.item, scale * C.owner_cap_month)
  const owner = coops + other
  const money = commission + n(counts, 'meetings_done') * values.meeting + n(counts, 'clients_followed') * values.client
  const productive = items > 0 && (closes + n(counts, 'meetings_done') + n(counts, 'clients_followed')) > 0
  const reposts = -C.repost_cost * n(counts, 'paid_reposts')
  const silent = sd ? -dayValue * (sd.silentRemote.length * C.remote_silent_weight
    + sd.silentOffice.length * C.office_silent_weight + sd.absent * C.absent_weight) : 0
  const late = sd ? -dayValue * ((sd.late || 0) * C.late_weight
    + (sd.lateMins || 0) / (sd.dayMins > 0 ? sd.dayMins : 480) * C.office_silent_weight) : 0
  const value = owner + money + reposts + silent + late
  return { value: Math.round(value), owner: Math.round(owner), money: Math.round(money), reposts: Math.round(reposts * 10) / 10 || 0,
    silent: Math.round(silent), late: Math.round(late) || 0, productive, items,
    coops: Math.round(coops), coopCount: coopCount(counts) }
}

// The office's value of a working day: median, over the reference, of (owner + money) per
// scheduled day. Comparative by construction — it rises and falls with what agents produce.
export function dayValueOf(rows) {
  const v = rows.filter(r => r.scheduledDays > 0).map(r => (r.owner + r.money) / r.scheduledDays).sort((a, b) => a - b)
  if (!v.length) return 0
  const m = v.length
  return Math.round(m % 2 ? v[(m - 1) / 2] : (v[m / 2 - 1] + v[m / 2]) / 2)
}

// The agent's average scheduled day in minutes, so late minutes can be priced as a share of a day.
// computeAgentStats sums scheduled minutes over worked days and returns the per-day worked average,
// so the day count is recovered from those two. No clock data → 8 hours. Clamped to 4–12 h so one
// odd day cannot make a minute late worth nothing or a whole day.
export function scheduledDayMins(stats) {
  const worked = Number(stats?.totalWorkedMins) || 0, avg = Number(stats?.avgWorkedMins) || 0
  const sched = Number(stats?.totalScheduledMins) || 0
  if (!(worked > 0 && avg > 0 && sched > 0)) return 480
  return Math.min(720, Math.max(240, Math.round(sched / (worked / avg))))
}

const dayOf = t => { const x = new Date(t); return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, '0')}-${String(x.getDate()).padStart(2, '0')}` }

// Per-agent inputs for one window — counts, ledger share, the ONE attendance definition (with the
// silent days), days on the board, whether the agent left any trace. Shared by every caller.
// ledgerByAgent: a caller that already built THE ledger for this window (the Stats tab: legacy
// viewings + core deals, overlap removed, rate history) passes its byAgent, so the score reads the
// exact deals and commission its Commission / Deals columns print (audit 2026-11-03: the score
// read core deals only, so a deal kept only as a legacy viewing raised commission but not score).
export function agentInputs({ roster, activity, deals, att, schedHist, since, until, fx, ledgerByAgent = null }) {
  const end = until ? new Date(until) : new Date()
  const windowDays = Math.max(1, (end - new Date(since)) / 86400000)
  const bySched = new Map((activity || []).filter(r => r.scheduler_agent_id).map(r => [r.scheduler_agent_id, r]))
  const led = ledgerByAgent || buildLedger(mergeDealSources([], deals || [], roster), null, null, fx, roster).byAgent
  const attBy = new Map()
  for (const r of att || []) { if (!r?.agent_id) continue; if (!attBy.has(r.agent_id)) attBy.set(r.agent_id, []); attBy.get(r.agent_id).push(r) }
  const from = dayOf(since), to = dayOf(end.getTime() - 1)
  const rows = []
  for (const a of roster) {
    if (a.is_active === false) continue
    const hire = a.hire_date ? new Date(a.hire_date + 'T00:00:00Z') : null
    const effDays = hire && hire > new Date(since) ? Math.max(0, (end - hire) / 86400000) : windowDays
    if (effDays <= 0) continue
    const counts = bySched.get(a.id) || {}
    const trace = new Set(counts.active_days || [])
    const m = led.get(a.id)
    const stats = computeAgentStats(a, attBy.get(a.id) || [], 5, from, to, schedHist || [], { traceDates: trace })
    rows.push({ agent: a, id: a.id, name: a.name, counts, closes: m?.participations || 0, commission: m?.commission || 0,
      stats, scheduledDays: stats.scheduledDays, effDays, isManager: isManagerRole(a.role),
      hasTrace: trace.size > 0 || (m?.participations || 0) > 0,
      sd: { silentRemote: stats.silentRemoteDates || [], silentOffice: stats.silentOfficeDates || [], absent: stats.absentDays || 0,
        late: stats.lateDays || 0, lateMins: stats.lateMins || 0, dayMins: scheduledDayMins(stats) } })
  }
  return { rows, windowDays }
}

// Score one window: rates (from the reference window, or this one), the office's value of a working
// day from the gated rows, then every agent's value. Returns rows with .value/.parts and the
// reference numbers the surfaces print.
export function valueWindow(inp, { values = null, cfg = null } = {}) {
  const { rows, windowDays } = inp
  const capScale = windowDays / 30
  const vals = values || deriveValues(rows.filter(r => inReference(r, cfg, { needSchedule: false })), cfg)
  const first = rows.map(r => ({ r, v: agentValue(r, vals, 0, cfg, { capScale: Math.max(1, r.effDays) / 30 }) }))
  const ref = first.filter(x => inReference(x.r, cfg))
  const dayValue = dayValueOf(ref.map(x => ({ scheduledDays: x.r.scheduledDays, owner: x.v.owner, money: x.v.money })))
  const out = first.map(({ r }) => {
    const v = agentValue(r, vals, dayValue, cfg, { capScale: Math.max(1, r.effDays) / 30 })
    return { ...r, value: v.value, score: v.value, parts: { owner: v.owner, coops: v.coops, results: v.money, reposts: v.reposts, silent: v.silent, late: v.late },
      productive: v.productive, items: v.items, coopCount: v.coopCount, inRef: inReference(r, cfg), mult: 1 }
  })
  out.sort((x, y) => y.value - x.value || (x.name || '').localeCompare(y.name || ''))
  return { rows: out, values: vals, dayValue, refSize: ref.length, capScale }
}
